import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkforceIntelligenceProvider } from "@/contexts/WorkforceIntelligenceContext";
import { PortalProvider } from "@/contexts/PortalContext";
import ProtectedRoute from "@/components/ProtectedRoute";

// Portal Pages
import {
  StudentDashboard as PortalDashboard,
  MyProgram, 
  MyCourses, 
  CourseDetail,
  AssignmentsList,
  AssignmentDetail,
  InstructorAssignments,
  InstructorSubmissions,
  InstructorGradebook
} from "@/pages/portal";

// Role-specific layouts and dashboards
import StudentLayout from "@/pages/portal/layouts/StudentLayout";
import InstructorLayout from "@/pages/portal/layouts/InstructorLayout";
import AdminLayout from "@/pages/portal/layouts/AdminLayout";
import InstructorDashboard from "@/pages/portal/InstructorDashboard";
import InstructorStudents from "@/pages/portal/InstructorStudents";
import AdminDashboard from "@/pages/portal/AdminDashboard";

// Portal Auth
import PortalLoginPage from "@/pages/auth/PortalLoginPage";

// Pages - Main
import Home from "@/pages/Home";
import About from "@/pages/About";
import Founder from "@/pages/Founder";
import Research from "@/pages/Research";
import Press from "@/pages/Press";
import MediaKit from "@/pages/MediaKit";
import Admissions from "@/pages/Admissions";
import Contact from "@/pages/Contact";

// New Main Pages (Work Order #1)
import MetaBook from "@/pages/MetaBook";
import MetaSolution from "@/pages/MetaSolution";
import SCFRJournal from "@/pages/SCFRJournal";
import SCFRArticle from "@/pages/SCFRArticle";

// Eco-System Pages
import EcoSystem from "@/pages/ecosystem/EcoSystem";
import EcoZodiacLMSLab from "@/pages/ecosystem/ZodiacLMSLab";
import EcoObservatory from "@/pages/ecosystem/Observatory";
import EcoLibrary from "@/pages/ecosystem/Library";
import EcoSocialMedia from "@/pages/ecosystem/SocialMedia";
import EcoPartnership from "@/pages/ecosystem/Partnership";

// WAIT Theory (Foundation)
import WAITTheory from "@/pages/WAITTheory";

// Zodiac Lab (Application Engine)
import ZodiacLab from "@/pages/zodiaclab/ZodiacLab";
import WorkforceLab from "@/pages/zodiaclab/WorkforceLab";
import OrganizationLab from "@/pages/zodiaclab/OrganizationLab";
import InstitutionalLab from "@/pages/zodiaclab/InstitutionalLab";
import OpportunityEngine from "@/pages/zodiaclab/OpportunityEngine";

// Observatories (Intelligence System)
import Observatories from "@/pages/observatories/Observatories";
import AIObservatory from "@/pages/observatories/AIObservatory";
import AICapabilityRegistry from "@/pages/observatories/AICapabilityRegistry";
import GlobalObservatoryLanding from "@/pages/observatory/Landing";
import GlobalObservatorySurvey from "@/pages/observatory/Survey";
import GlobalObservatoryResults from "@/pages/observatory/Results";
import GlobalObservatoryDashboard from "@/pages/observatory/Dashboard";
import GlobalObservatoryReports from "@/pages/observatory/Reports";
import WIIBenchmarkingDashboard from "@/pages/observatories/WIIBenchmarkingDashboard";

// Programs (Outputs)
import Programs from "@/pages/programs/Programs";
import ProgramDetails from "@/pages/programs/ProgramDetails";
import ProgramApplication from "@/pages/programs/ProgramApplication";
import ComparePrograms from "@/pages/programs/ComparePrograms";
import TrainingCertificates from "@/pages/programs/TrainingCertificates";
import ExecutiveProfessionalMasters from "@/pages/programs/ExecutiveProfessionalMasters";
import Licensing from "@/pages/programs/Licensing";
import Courses from "@/pages/programs/Courses";

// Research (Phase 1.1 Research page now owns /research; sub-route components below)
import ResearchOverview from "@/pages/research/ResearchOverview";
import ResearchReports from "@/pages/research/ResearchReports";
import GovernanceFramework from "@/pages/research/GovernanceFramework";

// Certificate Verification
import CertificateVerification from "@/pages/CertificateVerification";

// JusWork (Digital Platform Interface)
import JusWork from "@/pages/juswork/JusWork";
import ProLab from "@/pages/juswork/ProLab";
import StudentDashboard from "@/pages/juswork/StudentDashboard";
import WorkforceIntelligenceProfile from "@/pages/juswork/WorkforceIntelligenceProfile";
import AIEntrepreneurshipLab from "@/pages/juswork/labs/AIEntrepreneurshipLab";
import LabPage from "@/pages/juswork/labs/LabPage";

// WAIT Scoring Engine
import WAITDashboard from "@/pages/wait/WAITDashboard";

// Auth
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import AuthCallback from "@/pages/auth/AuthCallback";

// Admin
import AnalyticsDashboard from "@/pages/admin/AnalyticsDashboard";

// Internal Admin (Private)
import InternalAdminLoginPage from "@/pages/admin/AdminLoginPage";
import InternalAdminDashboard from "@/pages/admin/AdminDashboard";
import SCFRAdminGateway from "@/pages/admin/SCFRAdminGateway";

// Components
import Navigation from "@/components/Navigation";
import IntelligenceAssistantWidget from "@/components/IntelligenceAssistantWidget";

// Coming Soon placeholder for portal features
function ComingSoon({ title }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-500">This feature is coming soon!</p>
      </div>
    </div>
  );
}

// Phase A: Legacy `/portal/*` deprecation redirector.
// Forwards `/portal/<rest>` → `/student/<rest>` so old bookmarks/links still work.
// Bare `/portal` and `/portal/login` continue to behave as before via other routes.
function LegacyPortalRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/portal\/?/, "");
  const target = rest ? `/student/${rest}` : "/student/dashboard";
  return <Navigate to={target + location.search} replace />;
}

// AppRouter component to handle OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  // This runs synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Auth Routes - No Navigation */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Main Login - OTP based (Student/Instructor only) */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Portal Login - Role Selection (legacy, redirects to /login) */}
      <Route path="/portal/login" element={<LoginPage />} />
      
      {/* Internal Admin Routes - Private, no public link */}
      <Route path="/internal/admin" element={<InternalAdminLoginPage />} />
      <Route path="/internal/admin/dashboard" element={<InternalAdminDashboard />} />

      {/* SCFR-Journal Admin Gateway - Private, no public Navigation overlay */}
      <Route path="/internal/scfr-admin" element={<SCFRAdminGateway />} />
      
      {/* Certificate Verification - Public */}
      <Route path="/verify-certificate/:certificateId" element={<CertificateVerification />} />
      
      {/* Student Routes */}
      <Route path="/student/*" element={
        <PortalProvider>
          <Routes>
            <Route element={<StudentLayout />}>
              <Route path="dashboard" element={<PortalDashboard />} />
              <Route path="program" element={<MyProgram />} />
              <Route path="courses" element={<MyCourses />} />
              <Route path="courses/:courseId" element={<CourseDetail />} />
              <Route path="assignments" element={<AssignmentsList />} />
              <Route path="assignments/:assignmentId" element={<AssignmentDetail />} />
              <Route path="grades" element={<ComingSoon title="My Grades" />} />
              <Route path="announcements" element={<ComingSoon title="Announcements" />} />
              <Route path="messages" element={<ComingSoon title="Messages" />} />
              <Route path="ai-tutor" element={<ComingSoon title="AI Tutor" />} />
              <Route path="certificates" element={<ComingSoon title="Certificates" />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>
          </Routes>
        </PortalProvider>
      } />
      
      {/* Instructor Routes */}
      <Route path="/instructor/*" element={
        <PortalProvider>
          <Routes>
            <Route element={<InstructorLayout />}>
              <Route path="dashboard" element={<InstructorDashboard />} />
              <Route path="courses" element={<MyCourses />} />
              <Route path="courses/:courseId" element={<CourseDetail />} />
              <Route path="assignments" element={<InstructorAssignments />} />
              <Route path="assignments/:assignmentId" element={<AssignmentDetail />} />
              <Route path="submissions" element={<InstructorSubmissions />} />
              <Route path="submissions/:assignmentId" element={<InstructorSubmissions />} />
              <Route path="gradebook" element={<InstructorGradebook />} />
              <Route path="gradebook/:courseId" element={<InstructorGradebook />} />
              <Route path="students" element={<InstructorStudents />} />
              <Route path="students/:courseId" element={<InstructorStudents />} />
              <Route path="announcements" element={<ComingSoon title="Announcements" />} />
              <Route path="messages" element={<ComingSoon title="Messages" />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>
          </Routes>
        </PortalProvider>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <PortalProvider>
          <Routes>
            <Route element={<AdminLayout />}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="programs" element={<ComingSoon title="Manage Programs" />} />
              <Route path="courses" element={<ComingSoon title="Manage Courses" />} />
              <Route path="instructors" element={<ComingSoon title="Manage Instructors" />} />
              <Route path="students" element={<ComingSoon title="Manage Students" />} />
              <Route path="assignments" element={<ComingSoon title="All Assignments" />} />
              <Route path="gradebook" element={<ComingSoon title="All Grades" />} />
              <Route path="announcements" element={<ComingSoon title="Announcements" />} />
              <Route path="messages" element={<ComingSoon title="Messages" />} />
              <Route path="certificates" element={<ComingSoon title="Certificates" />} />
              <Route path="reports" element={<ComingSoon title="Reports" />} />
              <Route path="settings" element={<ComingSoon title="Settings" />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>
          </Routes>
        </PortalProvider>
      } />
      
      {/* Legacy Portal Routes — Deprecated.
          Forwards `/portal/<sub>` → `/student/<sub>` for backwards compatibility.
          Role-aware role-aware routing happens inside StudentLayout once the
          user lands at /student/*. Bookmarks continue to work. */}
      <Route path="/portal/*" element={<LegacyPortalRedirect />} />
      
      {/* Main App Routes - With Navigation */}
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
}

// Main app with navigation
function MainApp() {
  return (
    <>
      <Navigation />
      <Routes>
        {/* Main Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/admissions" element={<Admissions />} />
        <Route path="/about" element={<About />} />
        <Route path="/founder" element={<Founder />} />
        <Route path="/research" element={<Research />} />
        <Route path="/press" element={<Press />} />
        <Route path="/media-kit" element={<MediaKit />} />
        <Route path="/contact" element={<Contact />} />

        {/* New Main Pages - Work Order #1 */}
        <Route path="/meta-book" element={<MetaBook />} />
        <Route path="/meta-solution" element={<MetaSolution />} />
        <Route path="/scfr-journal" element={<SCFRJournal />} />
        
        {/* SCFR Article Individual Pages */}
        <Route path="/scfr/:slug" element={<SCFRArticle />} />

        {/* Eco-System Section */}
        <Route path="/ecosystem" element={<EcoSystem />} />
        <Route path="/ecosystem/juswork" element={<JusWork />} />
        <Route path="/ecosystem/zodiac-lms-lab" element={<EcoZodiacLMSLab />} />
        <Route path="/ecosystem/observatory" element={<EcoObservatory />} />
        <Route path="/ecosystem/library" element={<EcoLibrary />} />
        <Route path="/ecosystem/social-media" element={<EcoSocialMedia />} />
        <Route path="/ecosystem/partnership" element={<EcoPartnership />} />

        {/* WAIT Theory - Foundation */}
        <Route path="/wait-theory" element={<WAITTheory />} />
        
        {/* WAIT Scoring Engine */}
        <Route path="/wait-engine" element={<WAITDashboard />} />

        {/* Zodiac Lab - Application Engine */}
        <Route path="/zodiac-lab" element={<ZodiacLab />} />
        <Route path="/zodiac-lab/workforce" element={<WorkforceLab />} />
        <Route path="/zodiac-lab/workforce/opportunities" element={<OpportunityEngine />} />
        <Route path="/zodiac-lab/workforce/tracks/:trackId" element={<WorkforceLab />} />
        <Route path="/zodiac-lab/organization" element={<OrganizationLab />} />
        <Route path="/zodiac-lab/institutional" element={<InstitutionalLab />} />

        {/* Observatories - Intelligence System */}
        <Route path="/observatories" element={<Observatories />} />
        <Route path="/observatories/ai" element={<AIObservatory />} />
        <Route path="/observatories/ai/registry" element={<AICapabilityRegistry />} />
        <Route path="/observatories/global" element={<GlobalObservatoryLanding />} />
        <Route path="/observatories/global/survey" element={<GlobalObservatorySurvey />} />
        <Route path="/observatories/global/results" element={<GlobalObservatoryResults />} />
        <Route path="/observatories/global/dashboard" element={<GlobalObservatoryDashboard />} />
        <Route path="/observatories/global/reports" element={<GlobalObservatoryReports />} />
        <Route path="/observatories/workforce-dashboard" element={<WIIBenchmarkingDashboard />} />

        {/* Programs Section - Outputs */}
        <Route path="/programs" element={<Programs />} />
        <Route path="/programs/apply" element={<ProgramApplication />} />
        <Route path="/programs/compare" element={<ComparePrograms />} />
        <Route path="/programs/executive-professional-masters" element={<ExecutiveProfessionalMasters />} />
        <Route path="/programs/training-certificates" element={<TrainingCertificates />} />
        <Route path="/programs/courses" element={<Courses />} />
        <Route path="/programs/licensing" element={<Licensing />} />
        <Route path="/programs/:programSlug" element={<ProgramDetails />} />

        {/* Research Section (Phase 1.1 /research is registered above with Main Pages) */}
        <Route path="/research/overview" element={<ResearchOverview />} />
        <Route path="/research/reports" element={<ResearchReports />} />
        <Route path="/research/governance" element={<GovernanceFramework />} />

        {/* JusWork Section - Digital Platform Interface */}
        <Route path="/juswork" element={<JusWork />} />
        <Route path="/juswork/profile" element={<WorkforceIntelligenceProfile />} />
        <Route path="/juswork/prolab" element={<ProLab />} />
        <Route path="/juswork/prolab/ai-entrepreneurship" element={<AIEntrepreneurshipLab />} />
        <Route path="/juswork/prolab/:labId" element={<LabPage />} />
        
        {/* JusWork Dashboard - PROTECTED (requires auth) */}
        <Route path="/juswork/dashboard" element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        <Route path="/juswork/prolab/dashboard" element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        } />

        {/* Legacy Observatory Routes - Redirect to new Observatories structure */}
        <Route path="/juswork/observatory" element={<Navigate to="/observatories/global" replace />} />
        <Route path="/juswork/observatory/survey" element={<Navigate to="/observatories/global/survey" replace />} />
        <Route path="/juswork/observatory/results" element={<Navigate to="/observatories/global/results" replace />} />
        <Route path="/juswork/observatory/dashboard" element={<Navigate to="/observatories/global/dashboard" replace />} />
        <Route path="/juswork/observatory/reports" element={<Navigate to="/observatories/global/reports" replace />} />
        <Route path="/observatory" element={<Navigate to="/observatories/global" replace />} />
        <Route path="/observatory/*" element={<Navigate to="/observatories/global" replace />} />
        
        {/* Admin Routes - Protected */}
        <Route path="/admin/analytics" element={
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        } />
      </Routes>
      
      {/* Global Intelligence Assistant Widget */}
      <IntelligenceAssistantWidget />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <WorkforceIntelligenceProvider>
            <AppRouter />
          </WorkforceIntelligenceProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
